import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useProfilePhoto } from "../../providers/profilePhotoContext";
import { useRequestScope } from "../../hooks/useRequestScope";
import { useScopedState } from "../../hooks/useScopedState";
import { useExclusiveAction } from "../../hooks/useExclusiveAction";
import { profilePhotoService } from "../../services/profilePhotoService";
import { PhotoCrop, profilePhotoCropService } from "../../services/profilePhotoCropService";

export function useProfilePhotoPromptViewModel(service = profilePhotoService, cropService = profilePhotoCropService) {
  const profile = useProfilePhoto();
  const { pathname } = useLocation();
  const uid = profile?.uid || "";
  const scope = useRequestScope(uid, service);
  const [opened, setOpened] = useScopedState(uid, false);
  const [hidden, setHidden] = useScopedState(uid, false);
  const [resumeAt, setResumeAt] = useScopedState(uid, 0);
  const [now, setNow] = useState(Date.now);
  const [busy, setBusy] = useScopedState(uid, false);
  const [error, setError] = useScopedState<string | null>(uid, null);
  const [selection, setSelection] = useScopedState<File | "google" | null>(uid, null);
  const [crop, setCrop] = useScopedState<PhotoCrop | null>(uid, null);
  const [preview, setPreview] = useState<{ file: File; url: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const refresh = () => {
      const current = Date.now();
      setNow(current);
      if (current >= resumeAt && profile?.status !== "pending") setHidden(false);
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [resumeAt, profile?.status, setHidden]);
  useEffect(() => {
    const url = selection instanceof File ? URL.createObjectURL(selection) : null;
    setPreview(url && selection instanceof File ? { file: selection, url } : null);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [selection]);

  const safeRoute = /^\/(?:discover|profile|account)\/?$/.test(pathname);
  const eligible = Boolean(profile && !profile.isRacing && !profile.photoUrl && safeRoute && !hidden);
  const show = eligible && (opened || profile?.status === "pending");
  const showReminder = eligible && !show && profile?.status !== "dismissed" &&
    (profile?.reminderAfter || 0) <= now;
  const close = (dismiss = false) => {
    if (busy || !uid) return;
    setHidden(true);
    setResumeAt(dismiss ? Infinity : Date.now() + 24 * 60 * 60 * 1000);
    setOpened(false);
    setSelection(null);
    // Optional setup never blocks navigation when offline. The current mounted
    // session suppresses the prompt even if persisting the preference fails.
    void service.defer(uid, dismiss).catch(() => console.warn("Unable to save profile photo reminder preference."));
  };
  const save = useExclusiveAction(scope, async () => {
    if (!selection || (selection !== "google" && !crop)) return;
    const current = scope.capture();
    setBusy(true);
    setError(null);
    try {
      const prepared = selection === "google" ? selection : await cropService.prepare(selection, crop);
      if (!current()) return;
      await service.save(uid, prepared);
      if (current()) { setHidden(true); setResumeAt(Infinity); setOpened(false); setSelection(null); }
    } catch {
      if (current()) setError("Could not save your photo. Try again or choose Not now.");
    } finally { if (current()) setBusy(false); }
  });
  const choose = (file: File | undefined) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size > 8 * 1024 * 1024) {
      setError("Choose a JPG, PNG or WebP image up to 8 MB.");
      return;
    }
    setError(null);
    setCrop(null);
    setSelection(file);
  };
  return {
    show, showReminder, busy, error, inputRef, selection, crop, setCrop,
    readyToSave: selection === "google" || Boolean(selection && crop),
    cropFailed: () => { setCrop(null); setError("Could not load your photo. Choose another photo."); },
    preview: selection === "google" ? profile?.googlePhotoUrl : preview?.file === selection ? preview?.url : null,
    googlePhotoUrl: profile?.googlePhotoUrl,
    open: () => setOpened(true), close, save, choose,
    chooseGoogle: () => { setSelection("google"); setCrop(null); setError(null); },
  };
}
